// What a listing card says, decided in one place (listing browse plan,
// 2026-09-23). The card shows one badge at most, the price, one line of
// specs, the address and one grey line of who and when - nothing else.

import { numberFormat } from '@/lib/format';

export type CardBadge = 'sold' | 'reduced' | 'new';

/** A listing is "new" for its first two days on the site. */
export const NEW_FOR_MS = 48 * 60 * 60 * 1000;
/** A price cut is news for thirty days. */
export const REDUCED_FOR_MS = 30 * 24 * 60 * 60 * 1000;
/** Photos a card lets you swipe through; the rest are on the listing page. */
export const CARD_PHOTO_LIMIT = 5;

type Dated = Date | string | null | undefined;

const time = (value: Dated): number | null => {
  if (!value) return null;
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : null;
};

/**
 * When the listing went public: the first time it did (first_published_at,
 * kept by the database), not its latest approval - an edit that needs another
 * review is approved again, and that does not make a listing new.
 */
export function listedAt(listing: { first_published_at?: Dated; approved_at?: Dated; created_at?: Dated }): number | null {
  return time(listing.first_published_at) ?? time(listing.approved_at) ?? time(listing.created_at);
}

/**
 * The one badge on the photo. Sold outranks a price cut, which outranks
 * "new": two badges side by side were read as neither.
 */
export function cardBadge(
  listing: { status?: string | null; price?: unknown; price_reduced_from?: unknown; price_reduced_at?: Dated; first_published_at?: Dated; approved_at?: Dated; created_at?: Dated },
  now: number
): CardBadge | null {
  if (listing.status === 'sold') return 'sold';
  const reducedAt = time(listing.price_reduced_at);
  if (reducedAt !== null && now - reducedAt < REDUCED_FOR_MS && Number(listing.price_reduced_from) > Number(listing.price)) {
    return 'reduced';
  }
  const since = listedAt(listing);
  if (since !== null && now - since < NEW_FOR_MS) return 'new';
  return null;
}

export const BADGE_LABEL: Record<CardBadge, { en: string; fr: string }> = {
  sold: { en: 'Sold', fr: 'Vendu' },
  reduced: { en: 'Price reduced', fr: 'Prix réduit' },
  new: { en: 'New', fr: 'Nouveau' },
};

// A rental that found its tenant is "rented", and its cut is to the rent
// (rentals, step 4, 2026-09-24).
const RENTAL_BADGE_LABEL: Partial<Record<CardBadge, { en: string; fr: string }>> = {
  sold: { en: 'Rented', fr: 'Loué' },
  reduced: { en: 'Rent reduced', fr: 'Loyer réduit' },
};

export function badgeLabel(badge: CardBadge, lang: string, rent = false): string {
  const l = (rent && RENTAL_BADGE_LABEL[badge]) || BADGE_LABEL[badge];
  return lang === 'fr' ? l.fr : l.en;
}

const DAY = 24 * 60 * 60 * 1000;

/** "Listed 3 days ago" / "Publiée il y a 3 jours", in whole days, weeks, months. */
export function listedAgo(since: number | null, lang: string, now: number): string {
  if (since === null) return '';
  const fr = lang === 'fr';
  const days = Math.max(0, Math.floor((now - since) / DAY));
  const rtf = new Intl.RelativeTimeFormat(fr ? 'fr-CA' : 'en-CA', { numeric: 'auto' });
  let when: string;
  if (days < 7) when = rtf.format(-days, 'day');
  else if (days < 30) when = rtf.format(-Math.floor(days / 7), 'week');
  else if (days < 365) when = rtf.format(-Math.floor(days / 30), 'month');
  else when = rtf.format(-Math.floor(days / 365), 'year');
  return fr ? `Publiée ${when}` : `Listed ${when}`;
}

/** Who sells or lets it, short enough for the card's last line. */
export function sellerLine(listing: { owner_role?: string | null; owner_name?: string | null; listing_type?: string | null }, lang: string): string {
  if (listing.owner_role === 'realtor' && listing.owner_name) return listing.owner_name;
  if (lang === 'fr') return 'Particulier';
  return listing.listing_type === 'rent' ? 'Private landlord' : 'Private seller';
}

/** Rooms worth showing: none on land or a commercial space without any. */
export function roomCount(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Living area in square feet, "1,240 sq ft" / "1 240 pi²". */
export function areaLabel(value: unknown, lang: string): string | null {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  const fr = lang === 'fr';
  const num = numberFormat(fr ? 'fr-CA' : 'en-CA', { maximumFractionDigits: 0 }).format(n);
  return fr ? `${num} pi²` : `${num} sq ft`;
}

/** What a card shows, and nothing else, for the rows sent to the browser. */
export type CardData = {
  id: string;
  slug: string;
  province: string;
  city: string;
  address: string | null;
  title_en: string | null;
  title_fr: string | null;
  property_type: string | null;
  /** 'sale' or 'rent': a rent is a month's (rentals, step 4). */
  listing_type: 'sale' | 'rent';
  price: string | number | null;
  price_reduced_from: string | number | null;
  price_reduced_at: string | null;
  bedrooms: string | number | null;
  bathrooms: string | number | null;
  living_area: number | null;
  status: string | null;
  first_published_at: string | null;
  approved_at: string | null;
  created_at: string | null;
  owner_role: string | null;
  owner_name: string | null;
  /** The first CARD_PHOTO_LIMIT photos; the card counts all of them. */
  images: string[];
  photo_count: number;
};

const iso = (value: unknown): string | null => {
  if (!value) return null;
  const d = new Date(value as string);
  return Number.isFinite(d.getTime()) ? d.toISOString() : null;
};

/**
 * A search row cut down to the card. The row is the whole listing - contact
 * email and phone included - and a listing's contacts are shown only on its
 * page, to the visitors allowed to see them.
 */
export function toCardData(row: any): CardData {
  const images =
    Array.isArray(row.images) && row.images.length > 0 ? row.images
      : Array.isArray(row.photos) ? row.photos : [];
  return {
    id: String(row.id),
    slug: String(row.slug),
    province: String(row.province),
    city: String(row.city),
    address: row.address ?? null,
    title_en: row.title_en ?? null,
    title_fr: row.title_fr ?? null,
    property_type: row.property_type ?? null,
    listing_type: row.listing_type === 'rent' ? 'rent' : 'sale',
    price: row.price == null ? null : String(row.price),
    price_reduced_from: row.price_reduced_from == null ? null : String(row.price_reduced_from),
    price_reduced_at: iso(row.price_reduced_at),
    bedrooms: row.bedrooms == null ? null : String(row.bedrooms),
    bathrooms: row.bathrooms == null ? null : String(row.bathrooms),
    living_area: row.living_area == null ? null : Number(row.living_area),
    status: row.status ?? null,
    first_published_at: iso(row.first_published_at),
    approved_at: iso(row.approved_at),
    created_at: iso(row.created_at),
    owner_role: row.owner_role ?? null,
    // The name of a private seller is not shown anywhere and is not sent either.
    owner_name: row.owner_role === 'realtor' ? row.owner_name ?? null : null,
    images: images.slice(0, CARD_PHOTO_LIMIT),
    photo_count: images.length,
  };
}
